import React, { useMemo, useState } from 'react';
import { AlertTriangle, Info, MapPin, PanelRightOpen, PanelRightClose, Wand2 } from 'lucide-react';
import { TrackingMap } from './TrackingMap';
import { AggregatedView } from './AggregatedView';
import { RouteView, type SegmentContextEvent } from './RouteView';
import { PlaybackControls } from './PlaybackControls';
import { MapLegend } from './MapLegend';
import { OsrmSegmentMenu } from './OsrmSegmentMenu';
import { OsrmPreviewPanel } from './OsrmPreviewPanel';
import { LoadingOverlay } from '../shared/LoadingOverlay';
import { formatUserLabel } from '../../utils/mapHelpers';
import { useOsrmCorrection } from '../../hooks/useOsrmCorrection';
import { useNotification } from '@/hooks/useNotification';
import type {
  UserActivityItem,
  TrackingResponse,
  MapViewMode,
  PlaybackState,
} from '../../types/tracking';

interface MapPanelProps {
  viewMode: MapViewMode;
  activities: UserActivityItem[];
  selectedIds: Set<string>;
  trackingData: Map<string, TrackingResponse>;
  trackingLoading: boolean;
  // Playback
  playbackState: PlaybackState;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSetSpeed: (speed: number) => void;
  onSeekTo: (index: number) => void;
  onSelect: (userId: string, multiSelect: boolean) => void;
  // Panel toggle
  panelOpen: boolean;
  onTogglePanel: () => void;
  // OSRM path-correction inputs
  /**
   * When true, per-segment right-click bubbles up as an OSRM menu. Set
   * this from the parent based on `canEditTracking(user)` — viewers
   * never even see the menu. The server still guards every call.
   */
  canEditSegments: boolean;
  /**
   * Master switch for the entire OSRM UI (menu, pill, preview panel,
   * historical overlays). Off in environments where the Supabase Edge
   * Functions aren't deployed yet — keeps the surface completely
   * invisible instead of showing a menu that would 404.
   */
  osrmFeatureEnabled: boolean;
  /**
   * `YYYY-MM-DD` date currently displayed on the map. Passed to
   * `POST /sync-osrm-data` when the reviewer commits a correction. The
   * sync endpoint rejects today / future dates; we surface that as a
   * disabled state on the "Sync" button so the reviewer sees why.
   */
  trackingDate: string;
  /**
   * Called after an OSRM decision commits so the parent can refetch
   * tracking data — the segment's `is_osrm_applied` state (and the
   * modified distance) may have changed on the backend.
   */
  onOsrmDecisionCommitted?: (userId: string) => void;
}

export const MapPanel: React.FC<MapPanelProps> = ({
  viewMode,
  activities,
  selectedIds,
  trackingData,
  trackingLoading,
  playbackState,
  onPlay,
  onPause,
  onStop,
  onSetSpeed,
  onSeekTo,
  onSelect,
  panelOpen,
  onTogglePanel,
  canEditSegments,
  osrmFeatureEnabled,
  trackingDate,
  onOsrmDecisionCommitted,
}) => {
  const liveCount = activities.filter(a => a.is_live).length;
  const { showSuccess, showError, showInfo } = useNotification();

  // Build legend data for route view
  const legendEntries: { name: string; color: string }[] = [];
  if (viewMode === 'route') {
    let idx = 0;
    for (const userId of selectedIds) {
      const act = activities.find(a => a.user_id === userId);
      if (act) {
        const colors = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#ca8a04', '#e11d48'];
        legendEntries.push({
          name: formatUserLabel(act.name, act.employee_id),
          color: colors[idx % colors.length],
        });
        idx++;
      }
    }
  }

  // ── OSRM correction wiring ────────────────────────────────────────────
  const [menu, setMenu] = useState<SegmentContextEvent | null>(null);
  const {
    preview,
    busy,
    error,
    computeMatch,
    applyMatch,
    rejectMatch,
    syncSummary,
    clearPreview,
  } = useOsrmCorrection();

  // Resolve raw distance for the segment currently under review — so the
  // preview panel can show a delta against the original. We walk every
  // selected user's segments (typically only one selected in route view).
  const rawDistanceForPreview = useMemo(() => {
    if (!preview) return 0;
    for (const userId of selectedIds) {
      const data = trackingData.get(userId);
      if (!data) continue;
      for (const session of data.sessions) {
        const seg = session.segments.find((s) => s.id === preview.segmentId);
        if (seg) return seg.total_distance_meters;
      }
    }
    return 0;
  }, [preview, selectedIds, trackingData]);

  // Sync is only valid for past dates per the spec. Today / future / an
  // invalid string all disable the Sync button with an explanatory
  // tooltip so the reviewer sees why.
  const syncDisabledReason = useMemo<string | null>(() => {
    if (!trackingDate) return 'No date selected.';
    const parsed = new Date(`${trackingDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return 'Invalid date.';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed >= today) {
      return 'Sync is only available for past dates. Wait for the daily rollup and try again tomorrow.';
    }
    return null;
  }, [trackingDate]);

  const activeUserIdForSync = useMemo<string | null>(() => {
    if (selectedIds.size !== 1) return null;
    return [...selectedIds][0];
  }, [selectedIds]);

  /**
   * Diagnose why the right-click menu might not appear so the reviewer
   * gets instant feedback instead of "nothing happens". Three states:
   *
   *   - `ready`    → role + segment ids present, right-click works
   *   - `no-role`  → viewer / report role, feature intentionally hidden
   *   - `no-ids`   → backend hasn't deployed the OSRM data model changes
   *                  yet (segments lack the DB `id` field the endpoints
   *                  need), so the menu is disabled even though the user
   *                  has permission
   *   - `not-active` → wrong view mode / multi-user selection
   */
  const correctionStatus = useMemo<'ready' | 'no-role' | 'no-ids' | 'not-active'>(() => {
    if (!osrmFeatureEnabled) return 'not-active';
    if (viewMode !== 'route' || selectedIds.size !== 1) return 'not-active';
    if (!canEditSegments) return 'no-role';
    // Check whether the selected user's tracking data has segments with
    // ids attached — otherwise the API endpoints can't accept the segment
    // and the menu would fire into a void.
    const userId = [...selectedIds][0];
    const data = trackingData.get(userId);
    if (!data) return 'ready'; // still loading — assume ready
    const anySegmentHasId = data.sessions.some((session) =>
      session.segments.some((s) => typeof s.id === 'number'),
    );
    if (!anySegmentHasId && data.sessions.some((s) => s.segments.length > 0)) {
      return 'no-ids';
    }
    return 'ready';
  }, [viewMode, selectedIds, canEditSegments, trackingData]);

  const handleSegmentContext = (evt: SegmentContextEvent) => {
    // Right-clicking a different segment while a preview is on screen
    // implicitly cancels the previous preview — otherwise the reviewer
    // has to Dismiss first, which is annoying on a dense trail.
    if (preview && preview.segmentId !== evt.segmentId) {
      clearPreview();
    }
    setMenu(evt);
  };

  const handleCorrect = async (extend = false) => {
    if (!menu) return;
    setMenu(null);
    const result = await computeMatch(menu.segmentId, extend ? { extend: true } : undefined);
    if (result.ok) return;

    // Pick a copy that reflects the specific failure so the reviewer
    // understands whether the gap is too small (an intentional
    // server-side guard, not a real error), whether the next-segment
    // link is missing, or whether something actually went wrong.
    const status = result.error.status;
    const message = result.error.message;
    const isGapTooSmall = status === 400 && /distance between segments/i.test(message);
    const isNoNextSegment = status === 404 && extend;

    if (isGapTooSmall) {
      showInfo(
        'Gap Too Small',
        'Auto-correction only runs when the gap is at least 1 km — this stretch is shorter and doesn\'t need OSRM smoothing.',
      );
    } else if (isNoNextSegment) {
      showInfo(
        'No Next Segment',
        'This is the last segment in the check-in, so there\'s nothing to extend to.',
      );
    } else if (status === 403) {
      showError(
        'Not Authorised',
        'Your account doesn\'t have the LOCATION.TRACKING_EDITOR role required for path corrections.',
      );
    } else {
      showError(
        extend ? 'Extend Failed' : 'Correction Failed',
        message ||
          (extend
            ? 'Could not extend the corrected path to the next segment. Please try again.'
            : 'Could not compute the corrected path. Please try again.'),
      );
    }
  };

  const handleApply = async () => {
    const wasApplied = await applyMatch();
    if (wasApplied) {
      showSuccess('Correction Applied', 'Sync the daily summary to update reports.');
      // Refetch tracking so `is_osrm_applied` on the segment flips green.
      if (onOsrmDecisionCommitted) {
        for (const userId of selectedIds) onOsrmDecisionCommitted(userId);
      }
    } else {
      showError('Apply Failed', 'Could not apply the correction. Please try again.');
    }
  };

  const handleReject = async () => {
    const wasRejected = await rejectMatch();
    if (wasRejected) {
      showInfo('Correction Rejected', 'The original path will continue to be used.');
      if (onOsrmDecisionCommitted) {
        for (const userId of selectedIds) onOsrmDecisionCommitted(userId);
      }
    } else {
      showError('Reject Failed', 'Could not reject the correction. Please try again.');
    }
  };

  const handleSync = async () => {
    if (!activeUserIdForSync || !trackingDate || syncDisabledReason) return;
    const ok = await syncSummary(activeUserIdForSync, trackingDate);
    if (ok) {
      showSuccess('Summary Synced', 'The daily distance now reflects the correction.');
      clearPreview();
    } else {
      showError('Sync Failed', 'Could not sync the daily summary. Please try again.');
    }
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Map header */}
      <div className="px-4 py-2 bg-white border-b border-rgf-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-rgf-primary-600" />
          <span className="text-sm font-medium text-rgf-gray-900">
            {viewMode === 'aggregated'
              ? 'All Users'
              : selectedIds.size === 1
                ? (() => {
                    const userId = [...selectedIds][0];
                    const user = activities.find(a => a.user_id === userId);
                    return user ? formatUserLabel(user.name, user.employee_id) : '1 Selected';
                  })()
                : `${selectedIds.size} Users Selected`}
          </span>
          <span className="text-xs text-rgf-gray-500">
            — {liveCount} active on map
          </span>

          {/* OSRM correction status pill — hidden when the feature isn't
              applicable (aggregated view or multi-user). Explains up-front
              whether right-click is available and why not, so a reviewer
              never has to guess why the menu didn't appear. */}
          {correctionStatus === 'ready' && (
            <span
              className="ml-3 inline-flex items-center gap-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-2 py-0.5 text-[11px] font-medium"
              title="Right-click a segment to correct its path with OSRM"
            >
              <Wand2 className="h-3 w-3" />
              Right-click a segment to correct
            </span>
          )}
          {correctionStatus === 'no-role' && (
            <span
              className="ml-3 inline-flex items-center gap-1 rounded-full bg-rgf-gray-100 border border-rgf-gray-200 text-rgf-gray-600 px-2 py-0.5 text-[11px] font-medium"
              title="Segment correction requires the LOCATION.TRACKING_EDITOR role"
            >
              <Info className="h-3 w-3" />
              Segment correction: read-only
            </span>
          )}
          {correctionStatus === 'no-ids' && (
            <span
              className="ml-3 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[11px] font-medium"
              title="Backend hasn't attached segment ids to this response yet — correction unavailable until the OSRM data model rolls out here."
            >
              <AlertTriangle className="h-3 w-3" />
              Segment ids missing
            </span>
          )}
        </div>
        <button
          onClick={onTogglePanel}
          className="p-1.5 rounded-lg text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-700 transition-colors"
          title={panelOpen ? 'Hide employees panel' : 'Show employees panel'}
        >
          {panelOpen ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <PanelRightOpen className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Map container */}
      <div className="flex-1 relative">
        {trackingLoading && <LoadingOverlay message="Loading routes..." />}

        <TrackingMap resizeSignal={panelOpen}>
          {viewMode === 'aggregated' ? (
            <AggregatedView activities={activities} onSelect={onSelect} />
          ) : (
            <RouteView
              selectedIds={selectedIds}
              trackingData={trackingData}
              playbackPosition={playbackState.currentPosition}
              canEditSegments={canEditSegments}
              onSegmentContext={handleSegmentContext}
              previewSegmentId={preview?.segmentId ?? null}
              previewPath={preview?.path ?? null}
              osrmFeatureEnabled={osrmFeatureEnabled}
            />
          )}
        </TrackingMap>

        {/* Legend overlay */}
        {viewMode === 'route' && legendEntries.length > 1 && (
          <MapLegend entries={legendEntries} />
        )}

        {/* OSRM preview panel (top-right of the map) */}
        {viewMode === 'route' && preview && (
          <OsrmPreviewPanel
            preview={preview}
            rawDistanceMeters={rawDistanceForPreview}
            busy={busy}
            error={error}
            syncDisabledReason={syncDisabledReason}
            onApply={handleApply}
            onReject={handleReject}
            onSync={handleSync}
            onDismiss={clearPreview}
          />
        )}
      </div>

      {/* Playback controls (route view only, single employee) */}
      {viewMode === 'route' && selectedIds.size === 1 && (
        <PlaybackControls
          playbackState={playbackState}
          onPlay={onPlay}
          onPause={onPause}
          onStop={onStop}
          onSetSpeed={onSetSpeed}
          onSeekTo={onSeekTo}
        />
      )}

      {/* Right-click menu — rendered in document coordinates so it
          survives map pan/zoom without redraws. */}
      {menu && (
        <OsrmSegmentMenu
          x={menu.x}
          y={menu.y}
          segmentId={menu.segmentId}
          extendable={menu.extendable}
          busy={busy === 'match'}
          onClose={() => setMenu(null)}
          onCorrect={() => handleCorrect(false)}
          onExtend={() => handleCorrect(true)}
        />
      )}
    </div>
  );
};
